import React from 'react';
import classNames from 'classnames';

import { Icon } from '../Icon/Icon';

interface Props {
  id: string;
  label?: string;
  onChange: (value: string) => void;
  className?: string;
  options: string[];
  error?: string | boolean;
  defaultValue?: string;
  labelClassName?: string;
  children?: React.ReactNode;
  showErrorOutLine?: boolean; // Case where we need the ability to show error outline if the input is 1 of 2 inputs
  renderWarningLabel?: (index: number) => JSX.Element | null;
  renderLabelFor: (index: number) => JSX.Element;
  renderAfterLabel?: (index: number) => JSX.Element;
  isRecommended?: (index: number) => boolean; // Adding a recommended flag to the top right of the radio input option - used in PickAPlan step
  isOptionDisabled?: (index: number) => boolean;
}

// @NOTE Can use RadioInput in block form and pass through your own label
export const RadioInput = (props: Props) => {
  const {
    id,
    label,
    onChange,
    className,
    error,
    options,
    defaultValue,
    children,
    showErrorOutLine,
    renderWarningLabel,
    renderLabelFor,
    isRecommended,
    renderAfterLabel,
    isOptionDisabled,
  } = props;

  return (
    <fieldset
      className={classNames('RadioInput', className, { hasError: (!!error || showErrorOutLine) })}
    >
      <legend>
        {children}
        {!children && <span className="RadioInput-label">{label}</span>}
      </legend>

      {options.map((option, index) => {
        const radioId = `${id}-${index}`;
        const isChecked = defaultValue === option;

        const showRecommendedFlag = isRecommended && isRecommended(index);
        const isDisabled = isOptionDisabled && isOptionDisabled(index);

        return (
          <div className={classNames('RadioInput-option', { isDisabled })} key={radioId}>
            {showRecommendedFlag && (
              <div className="RadioInput-recommended">
                <Icon name="ic-recommended" />
              </div>
            )}

            <label htmlFor={radioId} className="RadioInput-option-label" key={radioId}>

              <input
                disabled={isDisabled}
                className="RadioInput-option-button"
                type="radio"
                id={radioId}
                name={radioId}
                checked={isChecked}
                onChange={() => {
                  if (isDisabled) {
                    return;
                  }

                  onChange(option);
                }}
              />

              <span className={classNames('RadioInput-option-check', { isChecked })} />

              <div className="RadioInput-option-labels">
                {renderLabelFor(index)}
              </div>
            </label>

            {renderWarningLabel && renderWarningLabel(index)}
            {renderAfterLabel && renderAfterLabel(index)}
          </div>
        );
      })}

      {/* Error message */}
      {error && (
      <div className="RadioInput-error">
        <Icon name="ic-exclamation" />
        <span>{error}</span>
      </div>
      )}
    </fieldset>
  );
};
